/**
 * InterStopFormModal — création / édition d'un inter-arrêt (A → B).
 * Les listes Origine/Destination proviennent exclusivement du référentiel Arrêts (`stops`).
 * Saisie UI en km / minutes ; envoi backend en mètres / secondes.
 */
import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { ActionButton } from "@/components/ds";
import { ArrowRight, Route } from "lucide-react";
import { kmToMeters, minutesToSeconds, metersToKmInput, secondsToMinInput } from "@/lib/stopsUnits";

const FIELD = "w-full border border-zinc-900 px-3 py-2 text-sm bg-white";
const LABEL = "block text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1";

export default function InterStopFormModal({ open, onClose, initial, stops, onSubmit, saving }) {
  const [f, setF] = useState({ origin_stop_id: "", destination_stop_id: "", km: "", min: "", active: true });
  const [errs, setErrs] = useState({});
  const isEdit = !!initial?.id;
  const activeStops = (stops || []).filter((s) => s.active !== false || s.id === initial?.origin_stop_id || s.id === initial?.destination_stop_id);

  useEffect(() => {
    if (open) {
      setF({
        origin_stop_id: initial?.origin_stop_id || "",
        destination_stop_id: initial?.destination_stop_id || "",
        km: metersToKmInput(initial?.distance_meters),
        min: secondsToMinInput(initial?.travel_time_seconds),
        active: initial?.active !== false,
      });
      setErrs({});
    }
  }, [open, initial]);

  const submit = (e) => {
    e.preventDefault();
    const v = {};
    if (!f.origin_stop_id) v.origin = "Sélectionnez un arrêt d'origine.";
    if (!f.destination_stop_id) v.destination = "Sélectionnez un arrêt de destination.";
    if (f.origin_stop_id && f.origin_stop_id === f.destination_stop_id) v.destination = "L'origine et la destination doivent être différentes.";
    const meters = kmToMeters(f.km);
    const seconds = minutesToSeconds(f.min);
    if (meters == null) v.km = "Distance invalide (en km, ≥ 0).";
    if (seconds == null) v.min = "Temps invalide (en minutes, ≥ 0).";
    setErrs(v);
    if (Object.keys(v).length) return;
    onSubmit({ origin_stop_id: f.origin_stop_id, destination_stop_id: f.destination_stop_id,
      distance_meters: meters, travel_time_seconds: seconds, active: f.active });
  };

  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const StopSelect = ({ k, testId }) => (
    <select data-testid={testId} className={FIELD} value={f[k]} onChange={set(k)}>
      <option value="">— Choisir un arrêt —</option>
      {activeStops.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
    </select>
  );

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg border border-zinc-900 rounded-none p-0" data-testid="inter-stop-form-modal">
        <DialogHeader className="bg-zinc-900 text-white px-4 py-3">
          <DialogTitle className="font-display uppercase tracking-tight flex items-center gap-2">
            <Route size={16} className="text-[#FFC60A]" /> {isEdit ? "Modifier l'inter-arrêt" : "Nouvel inter-arrêt"}
          </DialogTitle>
          <DialogDescription className="text-zinc-400 text-xs">
            Liaison entre deux arrêts du référentiel. Distance et temps sont saisis manuellement (pas de routage automatique).
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="p-4 space-y-3">
          {initial?.reverse_of && (
            <div className="border border-orange-300 bg-orange-50 p-2 text-xs" data-testid="inter-stop-form-reverse-notice">
              Liaison inverse proposée à partir de <b>{initial.reverse_of}</b>. Distance et temps sont <b>pré-remplis à titre indicatif</b> : A→B et B→A peuvent différer — vérifiez et corrigez avant de valider.
            </div>
          )}
          <div className="grid grid-cols-[1fr_auto_1fr] gap-2 items-end">
            <div>
              <label className={LABEL}>Arrêt origine *</label>
              <StopSelect k="origin_stop_id" testId="inter-stop-form-origin" />
              {errs.origin && <p data-testid="inter-stop-form-error-origin" className="text-red-600 text-xs mt-1">{errs.origin}</p>}
            </div>
            <ArrowRight size={16} className="mb-3 text-zinc-500" />
            <div>
              <label className={LABEL}>Arrêt destination *</label>
              <StopSelect k="destination_stop_id" testId="inter-stop-form-destination" />
              {errs.destination && <p data-testid="inter-stop-form-error-destination" className="text-red-600 text-xs mt-1">{errs.destination}</p>}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={LABEL}>Distance (km) *</label>
              <input data-testid="inter-stop-form-km" className={`${FIELD} font-mono`} value={f.km} onChange={set("km")} placeholder="4.8" inputMode="decimal" />
              {errs.km && <p data-testid="inter-stop-form-error-km" className="text-red-600 text-xs mt-1">{errs.km}</p>}
            </div>
            <div>
              <label className={LABEL}>Temps de parcours (min) *</label>
              <input data-testid="inter-stop-form-min" className={`${FIELD} font-mono`} value={f.min} onChange={set("min")} placeholder="9" inputMode="decimal" />
              {errs.min && <p data-testid="inter-stop-form-error-min" className="text-red-600 text-xs mt-1">{errs.min}</p>}
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input data-testid="inter-stop-form-active" type="checkbox" checked={f.active} onChange={set("active")} />
            Inter-arrêt actif
          </label>
          {activeStops.length < 2 && (
            <p className="text-xs text-orange-700" data-testid="inter-stop-form-need-stops">
              Au moins deux arrêts actifs sont nécessaires dans le référentiel Arrêts.
            </p>
          )}
          <div className="flex justify-end gap-2 pt-2 border-t border-zinc-200">
            <ActionButton variant="ghost" onClick={onClose} testId="inter-stop-form-cancel">Annuler</ActionButton>
            <ActionButton variant="primary" type="submit" loading={saving} testId="inter-stop-form-submit">
              {isEdit ? "Enregistrer" : "Créer l'inter-arrêt"}
            </ActionButton>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
